import {
  QModal,
  QModalBody,
  QModalHeader,
  QBox,
  QText,
  QButton,
  QStack,
  QFormControl,
  QInput,
  QMultiSelect,
  useCurrentUser,
  useFeatureFlag,
  useToastProvider,
} from '@qualio/ui-components';
import React, { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CreateTemplateSchema, ZCreateTemplateSchema } from '../../types';
import { useMutation } from 'react-query';
import templateApi from '../../services/templateApi';
import { AxiosError } from 'axios';
import { extractMessageFromError, isPrefixInUseByParentError } from '../../utils/backEndErrorMessages';
import { filterTagOption, useTagOptions } from '../../hooks/useTagOptions';
import { zodResolver } from '@hookform/resolvers/zod';

type CreateTemplateModalProps = {
  onClose: (v: boolean) => void;
};

export const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = useCurrentUser();
  const { showToast } = useToastProvider();
  const templateDefaultTags = Boolean(useFeatureFlag()['template-default-tags']);
  const { options: tagOptions, isLoading: isLoadingTags } = useTagOptions();

  const formMethods = useForm<CreateTemplateSchema>({
    mode: 'onSubmit',
    resolver: zodResolver(ZCreateTemplateSchema),
  });

  const {
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = formMethods;

  const formHasErrors = !!Object.keys(errors).length;

  const { mutate: createTemplateMutate, isLoading: isCreatingTemplate } = useMutation(
    (payload: CreateTemplateSchema) =>
      templateApi.createNewTemplate(companyId, payload.prefix, payload.title, payload.tag_ids),
    {
      onSuccess: (data) => {
        showToast({
          description: t('template.create.success'),
          status: 'success',
          title: t('common.toast.success'),
          'data-cy': 'toast-create-template-success',
        });
        navigate(`/doc-templates/${data.id}`);
      },
      onError: (error: AxiosError) => {
        const message = extractMessageFromError(error);
        if (isPrefixInUseByParentError(error)) {
          setError('prefix', {
            type: 'server',
            message: t('template.create.errors.prefixInUseByParent'),
          });
        } else if (message.includes('Template prefix is already in use')) {
          setError('prefix', {
            type: 'server',
            message: t('template.create.errors.prefixInUse'),
          });
        } else if (message.includes('Template title is already in use')) {
          setError('title', {
            type: 'server',
            message: t('template.create.errors.titleInUse'),
          });
        } else {
          showToast({
            description: message,
            status: 'error',
            title: t('common.toast.error'),
            'data-cy': 'toast-create-template-error',
          });
        }
      },
    },
  );

  const onSubmit = useCallback(
    async (payload: CreateTemplateSchema) => {
      createTemplateMutate(payload);
    },
    [createTemplateMutate],
  );

  const handleClose = useCallback(() => {
    onClose(false);
  }, [onClose]);

  return (
    <QModal isOpen={true} onClose={handleClose} size="xl">
      <QModalHeader>
        <QText data-cy="create-template-modal-header">{t('template.create.modalTitle')}</QText>
      </QModalHeader>
      <QModalBody>
        <form data-cy="form">
          <QStack spacing="24px">
            <QFormControl
              label={t('template.create.titleLabel')}
              error={errors.title && errors.title.message}
              isInvalid={!!errors.title}
              isRequired
            >
              <Controller
                control={control}
                name="title"
                render={(controllerProps) => <QInput {...controllerProps.field} data-cy="title-field" />}
              />
            </QFormControl>
            <QFormControl
              label={t('template.create.prefixLabel')}
              error={errors.prefix && errors.prefix.message}
              isInvalid={!!errors.prefix}
              isRequired
              helper={t('template.create.prefixHelper')}
            >
              <Controller
                control={control}
                name="prefix"
                render={(controllerProps) => <QInput {...controllerProps.field} data-cy="prefix-field" />}
              />
            </QFormControl>
            {templateDefaultTags && (
              <QFormControl
                label={t('template.properties.fields.defaultTags')}
                error={errors.tag_ids?.message}
                isInvalid={!!errors.tag_ids}
                helper={t('template.properties.helpers.defaultTags')}
                data-cy="default-tags-field"
              >
                <Controller
                  control={control}
                  name="tag_ids"
                  render={({ field: { ref: _ref, onChange, ...field } }) => (
                    <QMultiSelect
                      {...field}
                      size="sm"
                      onChange={(selected) => onChange(selected.map((option) => option.value))}
                      options={tagOptions}
                      filterOption={filterTagOption}
                      isLoading={isLoadingTags}
                      isClearable
                      aria-label={t('template.properties.fields.defaultTags')}
                    />
                  )}
                />
              </QFormControl>
            )}
          </QStack>
          <QBox mt={5} mb={3} display="flex" flexDirection="row" justifyContent="flex-end">
            <QBox mr={3}>
              <QButton variant="outline" data-cy="cancel-button" onClick={handleClose}>
                {t('common.buttons.cancel')}
              </QButton>
            </QBox>
            <QButton
              data-cy="submit-button"
              type="submit"
              isDisabled={formHasErrors || isCreatingTemplate}
              onClick={handleSubmit(onSubmit)}
            >
              {t('template.create.buttonLabel')}
            </QButton>
          </QBox>
        </form>
      </QModalBody>
    </QModal>
  );
};
